import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Building, User, Mail, Lock, ArrowLeft, Sparkles, ShieldCheck, Rocket } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";
import { getReferralCode, setReferralCookie } from "@/lib/referral";
import { Seo } from "@/components/seo/Seo";
import { pushGtmEvent } from "@/lib/analytics";


const createAuthSchema = (t: (key: string, fallback?: string) => string) =>
  z.object({
    email: z.string().email(t("auth.validation.email")),
    password: z.string().min(6, t("auth.validation.password")),
    user_type: z.enum(["customer", "company"]),
    full_name: z.string().optional(),
    accept_terms: z.boolean().optional(),
    accept_privacy: z.boolean().optional(),
  });

type AuthForm = z.infer<ReturnType<typeof createAuthSchema>>;

export default function Auth() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const oauthLoginTracked = useRef(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, lang } = useI18n();
  const refCode = getReferralCode();
  const authSchema = useMemo(() => createAuthSchema(t), [t]);

  const form = useForm<AuthForm>({
    resolver: zodResolver(authSchema),
    defaultValues: {
      email: "",
      password: "",
      user_type: "customer",
      full_name: "",
      accept_terms: false,
      accept_privacy: false,
    },
  });

  useEffect(() => {
    const userType = searchParams.get("type");
    const verified = searchParams.get("verified");
    const oauth = searchParams.get("oauth");
    const desiredType = searchParams.get("user_type");
    
    if (userType === "company") {
      form.setValue("user_type", "company");
    }

    // Persist referral code from URL into cookie for the rest of the onboarding flow.
    const refFromUrl = searchParams.get('ref');
    if (refFromUrl) setReferralCookie(refFromUrl);
    
    if (verified === "true") {
      toast({
        title: t("auth.toast.email_verified_title"),
        description: t("auth.toast.email_verified_desc"),
      });
    }

    // Post-OAuth: ensure profile exists and set desired user_type if provided
    const postOAuth = async () => {
      if (!oauth) return;
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      if (!session) return;

      try {
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("id, user_type")
          .eq("user_id", session.user.id)
          .single();

        if (profileError && profileError.code !== "PGRST116") throw profileError;

        // Create profile if missing
        let profileId = profile?.id as string | undefined;
        if (!profileId) {
          const { data: created, error: createErr } = await supabase
            .from("profiles")
            .insert({
              user_id: session.user.id,
              email: session.user.email,
              full_name: session.user.user_metadata?.full_name || session.user.email?.split("@")[0] || "User",
              user_type: (desiredType === "company" ? "company" : "customer") as any,
            })
            .select("id")
            .single();
          if (createErr) throw createErr;
          profileId = created?.id;
        } else if (desiredType === "company" && profile?.user_type !== "company") {
          await supabase.from("profiles").update({ user_type: "company" }).eq("id", profileId);
        }

        if (!oauthLoginTracked.current) {
          oauthLoginTracked.current = true;
          pushGtmEvent("login", { method: "google_oauth", user_type: desiredType === "company" ? "company" : "customer" });
        }
        // Redirect after OAuth
        if (desiredType === "company") {
          navigate("/company/onboarding");
        } else {
          navigate("/dashboard");
        }
      } catch (e: any) {
        console.error("Post-OAuth handling failed", e);
      }
    };

    postOAuth();
  }, [searchParams, form, toast, t, navigate]);



  const handleResendVerification = async () => {
    const email = form.getValues("email");
    if (!email) {
      toast({
        title: t("auth.toast.error"),
        description: t("auth.toast.email_required"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth?verified=true`,
        },
      });

      if (error) throw error;

      toast({
        title: t("auth.toast.verification_sent_title"),
        description: t("auth.toast.verification_sent_desc"),
      });
    } catch (error: unknown) {
      const err = error as { message?: string };
      const msg = String(err?.message || "").toLowerCase();
      if (msg.includes("rate limit")) {
        toast({
          title: t("auth.toast.resend_rate_limit_title"),
          description: t("auth.toast.resend_rate_limit_desc"),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("auth.toast.error"),
          description: err?.message || t("auth.toast.resend_failed"),
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: AuthForm) => {
    console.log("Form submitted with data:", data);
    console.log("Is sign up:", isSignUp);
    
    // Validate required fields for sign up only
    if (isSignUp && !data.full_name?.trim()) {
      toast({
        title: t("auth.toast.error"),
        description: t("auth.toast.full_name_required"),
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    console.log("Starting authentication process...", { isSignUp, email: data.email });
    
    try {
      if (isSignUp) {
        if (!data.accept_terms || !data.accept_privacy) {
          toast({
            title: t("auth.toast.accept_legal_title"),
            description: t("auth.toast.accept_legal_desc"),
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        // Sign up - the trigger will automatically create the profile
        console.log("Attempting sign up...");
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            data: {
              full_name: data.full_name,
              user_type: data.user_type,
              ...(refCode ? { ref_code: refCode } : {}),
            },
            emailRedirectTo: `${window.location.origin}/auth?verified=true`,
          },
        });

        if (authError) {
          console.error("Sign up error:", authError);
          throw authError;
        }

        console.log("Sign up successful:", authData);

        if (authData.user) {
          pushGtmEvent("sign_up", { method: "password", user_type: data.user_type });
          // Record consent versions at signup
          try {
            const { LEGAL_VERSIONS } = await import("@/lib/legal");
            await (supabase as any)
              .from("profiles")
              .update({
                terms_version: (LEGAL_VERSIONS as any)?.terms || 'v1',
                privacy_version: (LEGAL_VERSIONS as any)?.privacy || 'v1',
                consented_at: new Date().toISOString(),
                consent_source: 'signup'
              })
              .eq("user_id", authData.user.id);
          } catch {}
          // Check if email confirmation is required
          if (authData.user.email_confirmed_at) {
            // Email already confirmed, redirect immediately
            toast({
              title: t("auth.toast.account_created_title"),
              description: t("auth.toast.welcome"),
            });

            if (data.user_type === "company") {
              navigate("/company/onboarding");
            } else {
              navigate("/dashboard");
            }
          } else {
            // Email confirmation required
            toast({
              title: t("auth.toast.account_created_title"),
              description: t("auth.toast.check_email"),
            });
            
            // Stay on the auth page but show a message
            setIsSignUp(false);
          }
        }
      } else {
        // Sign in
        console.log("Attempting sign in...");
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password,
        });

        console.log("Sign in response:", { authData, authError });

        if (authError) {
          console.error("Sign in error:", authError);
          // Check if it's an email confirmation error
          if (authError.message.includes("Email not confirmed")) {
            toast({
              title: t("auth.toast.email_not_verified_title"),
              description: t("auth.toast.email_not_verified_desc"),
              variant: "destructive",
            });
            return;
          }
          throw authError;
        }

        if (authData.user) {
          console.log("Auth user:", authData.user); // Debug log
          pushGtmEvent("login", { method: "password" });

          // Check user type and redirect accordingly
          console.log("Fetching user profile...");
          const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("id, user_type")
            .eq("user_id", authData.user.id)
            .single();

          console.log("Profile fetch result:", { profile, profileError });

          if (profileError) {
            console.error("Profile fetch error:", profileError);
            
            // If profile doesn't exist, create one with default values
            if (profileError.code === 'PGRST116') { // No rows returned
              console.log("Profile not found, creating default profile...");
              
              const { data: newProfile, error: createError } = await supabase
                .from("profiles")
                .insert({
                  user_id: authData.user.id,
                  email: authData.user.email,
                  full_name: authData.user.email?.split('@')[0] || 'User',
                  user_type: 'customer', // Default to customer
                })
                .select("id, user_type")
                .single();

              if (createError) {
                console.error("Profile creation error:", createError);
                toast({
                  title: t("auth.toast.error"),
                  description: t("auth.toast.profile_create_failed"),
                  variant: "destructive",
                });
                return;
              }

              console.log("Created new profile:", newProfile);
              
              // Redirect to customer dashboard
              navigate("/dashboard");
              toast({
                title: t("auth.toast.welcome_back_title"),
                description: t("auth.toast.welcome_back_desc"),
              });
              return;
            }
            
            // For other profile errors, show generic error
            toast({
              title: t("auth.toast.error"),
              description: t("auth.toast.profile_load_failed"),
              variant: "destructive",
            });
            return;
          }

          console.log("User profile:", profile); // Debug log

          if (profile?.user_type === "company") {
            console.log("User is a company, checking company record..."); // Debug log
            
            // Resolve company like dashboard: prefer latest with onboarding completed (multiple companies => avoid .single()).
            let company: { id: string; onboarding_completed: boolean | null } | null = null;
            const { data: completedCos, error: compErr1 } = await supabase
              .from("companies")
              .select("id, onboarding_completed")
              .eq("profile_id", profile.id)
              .eq("is_deleted", false)
              .eq("is_active", true)
              .eq("onboarding_completed", true)
              .order("created_at", { ascending: false })
              .limit(1);
            if (compErr1 && compErr1.code !== "PGRST116") {
              console.error("Company fetch error:", compErr1);
            }
            if (completedCos && completedCos.length > 0) {
              company = completedCos[0] as { id: string; onboarding_completed: boolean | null };
            } else {
              const { data: activeCos, error: compErr2 } = await supabase
                .from("companies")
                .select("id, onboarding_completed")
                .eq("profile_id", profile.id)
                .eq("is_deleted", false)
                .eq("is_active", true)
                .order("created_at", { ascending: false })
                .limit(1);
              if (compErr2 && compErr2.code !== "PGRST116") {
                console.error("Company fetch error:", compErr2);
              }
              if (activeCos && activeCos.length > 0) {
                company = activeCos[0] as { id: string; onboarding_completed: boolean | null };
              }
            }

            console.log("Company data:", company); // Debug log

            if (company?.onboarding_completed) {
              console.log("Company onboarding completed, redirecting to dashboard"); // Debug log
              navigate("/company/dashboard");
            } else {
              console.log("Company onboarding not completed, redirecting to onboarding"); // Debug log
              navigate("/company/onboarding");
            }
          } else {
            console.log("User is a customer, redirecting to dashboard"); // Debug log
            navigate("/dashboard");
          }

          toast({
            title: t("auth.toast.welcome_back_title"),
            description: t("auth.toast.welcome_back_desc"),
          });
        } else {
          console.log("No user data returned from sign in");
        }
      }
    } catch (error: unknown) {
      const err = error as { message?: string };
      console.error("Authentication error:", err);
      const msg = String(err?.message || "").toLowerCase();
      if (isSignUp && msg.includes("rate limit")) {
        toast({
          title: t("auth.toast.signup_rate_limit_title"),
          description: t("auth.toast.signup_rate_limit_desc"),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("auth.toast.error"),
          description: err?.message || t("auth.toast.unexpected_error"),
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async () => {
    try {
      setIsLoading(true);
      const userType = form.getValues("user_type");
      await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth?oauth=1&user_type=${userType}`,
          scopes: "openid email profile",
          queryParams: { prompt: "consent", access_type: "offline" },
        },
      });
      // Browser will redirect; no further action here
    } catch (error: any) {
      toast({
        title: t("auth.toast.google_failed_title"),
        description: (error as { message?: string })?.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div key={lang} className="app-page flex items-center justify-center p-4 md:p-6">
      <Seo title={t("auth.seo.title")} noindex canonicalPath="/auth" />
      <div className="w-full max-w-5xl">
        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="app-dark-panel hidden min-h-[720px] flex-col justify-between p-8 lg:flex">
            <div className="space-y-6">
              <Badge className="w-fit border-white/10 bg-white/8 text-slate-100 hover:bg-white/8">
                {t("auth.badge")}
              </Badge>
              <div className="space-y-4">
                <h1 className="text-4xl font-semibold leading-tight text-white">{t("auth.panel.title")}</h1>
                <p className="max-w-xl text-base leading-7 text-slate-300">{t("auth.panel.subtitle")}</p>
              </div>
              <div className="grid gap-4">
                <div className="app-dark-metric flex items-start gap-3 p-4">
                  <Sparkles className="mt-0.5 h-5 w-5 text-sky-300" />
                  <div>
                    <div className="font-semibold text-white">{t("auth.panel.setup.title")}</div>
                    <p className="text-sm text-slate-300">{t("auth.panel.setup.desc")}</p>
                  </div>
                </div>
                <div className="app-dark-metric flex items-start gap-3 p-4">
                  <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-300" />
                  <div>
                    <div className="font-semibold text-white">{t("auth.panel.trust.title")}</div>
                    <p className="text-sm text-slate-300">{t("auth.panel.trust.desc")}</p>
                  </div>
                </div>
                <div className="app-dark-metric flex items-start gap-3 p-4">
                  <Rocket className="mt-0.5 h-5 w-5 text-violet-300" />
                  <div>
                    <div className="font-semibold text-white">{t("auth.panel.convert.title")}</div>
                    <p className="text-sm text-slate-300">{t("auth.panel.convert.desc")}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="app-dark-metric grid grid-cols-3 gap-4 p-5">
              <div>
                <div className="text-2xl font-bold text-white">2</div>
                <div className="text-sm text-slate-400">{t("auth.panel.stat.paths")}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">1</div>
                <div className="text-sm text-slate-400">{t("auth.panel.stat.access")}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">0</div>
                <div className="text-sm text-slate-400">{t("auth.panel.stat.friction")}</div>
              </div>
            </div>
          </div>

          <Card className="app-panel relative overflow-hidden border border-white/60">
          <CardHeader className="text-center">
            <div className="absolute right-4 top-4">
              <LanguageSwitcher compact />
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="absolute left-4 top-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("auth.back")}
            </Button>
            <div className="mx-auto mb-4 inline-flex rounded-full border border-slate-200/70 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {t("auth.brand_badge")}
            </div>
            <CardTitle className="text-2xl">
              {isSignUp ? t("auth.title.create") : t("auth.title.welcome")}
            </CardTitle>
            <CardDescription>
              {isSignUp ? t("auth.subtitle.signup") : t("auth.subtitle.signin")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs 
              value={form.watch("user_type")} 
              onValueChange={(value) => form.setValue("user_type", value as "customer" | "company")}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="customer" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t("auth.tabs.customer")}
                </TabsTrigger>
                <TabsTrigger value="company" className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  {t("auth.tabs.company")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="customer" className="mt-6 space-y-4">
                <div className="rounded-[1.25rem] border border-slate-200/70 bg-white/80 p-4 shadow-soft">
                  <div className="text-sm font-semibold text-slate-900">{t("auth.path.customer.title")}</div>
                  <p className="mt-1 text-sm text-slate-600">{t("auth.path.customer.desc")}</p>
                </div>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
                    console.log("Form validation errors:", errors);
                    toast({
                      title: t("auth.toast.validation_title"),
                      description: t("auth.toast.validation_desc"),
                      variant: "destructive",
                    });
                  })} className="space-y-4">
                    {isSignUp && (
                      <FormField
                        control={form.control}
                        name="full_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("auth.form.full_name")}</FormLabel>
                            <FormControl>
                              <Input placeholder="John Doe" autoComplete="name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("auth.form.email")}</FormLabel>
                          <FormControl>
                            <Input 
                              type="email" 
                              placeholder="john@example.com" 
                              autoComplete="email"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                            <FormLabel>{t("auth.form.password")}</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder="••••••••" 
                              autoComplete={isSignUp ? "new-password" : "current-password"}
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {isSignUp && (
                      <div className="space-y-2">
                        <FormField
                          control={form.control}
                          name="accept_terms"
                          render={({ field }) => (
                            <FormItem className="flex items-start gap-3">
                              <FormControl>
                                <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {t("auth.terms.prefix")}{" "}
                                <Link to="/legal/terms" className="underline">{t("auth.terms.link")}</Link>
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="accept_privacy"
                          render={({ field }) => (
                            <FormItem className="flex items-start gap-3">
                              <FormControl>
                                <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {t("auth.privacy.prefix")}{" "}
                                <Link to="/legal/privacy" className="underline">{t("auth.privacy.link")}</Link>
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={isLoading}
                      onClick={() => console.log("Button clicked, form data:", form.getValues())}
                    >
                      {isLoading
                        ? t("auth.btn.loading")
                        : isSignUp
                          ? t("auth.btn.create")
                          : t("auth.btn.signin")}
                    </Button>
                    <div className="mt-4">
                      <Button type="button" variant="outline" className="w-full" onClick={handleGoogle}>
                        {t("auth.btn.google")}
                      </Button>
                    </div>
                  </form>
                </Form>
              </TabsContent>

              <TabsContent value="company" className="mt-6 space-y-4">
                <div className="rounded-[1.25rem] border border-sky-200/60 bg-sky-50/80 p-4 shadow-soft">
                  <div className="text-sm font-semibold text-slate-900">{t("auth.path.company.title")}</div>
                  <p className="mt-1 text-sm text-slate-600">{t("auth.path.company.desc")}</p>
                </div>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
                    console.log("Form validation errors:", errors);
                    toast({
                      title: t("auth.toast.validation_title"),
                      description: t("auth.toast.validation_desc"),
                      variant: "destructive",
                    });
                  })} className="space-y-4">
                    {isSignUp && (
                      <FormField
                        control={form.control}
                        name="full_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("auth.form.company_name")}</FormLabel>
                            <FormControl>
                              <Input placeholder="Es. Impresa Pulita Venezia" autoComplete="organization" {...field} />
                            </FormControl>
                            <p className="text-xs text-muted-foreground">{t("auth.form.company_hint")}</p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("auth.form.email")}</FormLabel>
                          <FormControl>
                            <Input 
                              type="email" 
                              placeholder="company@example.com" 
                              autoComplete="email"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                            <FormLabel>{t("auth.form.password")}</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder="••••••••" 
                              autoComplete={isSignUp ? "new-password" : "current-password"}
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {isSignUp && (
                      <div className="space-y-2">
                        <FormField
                          control={form.control}
                          name="accept_terms"
                          render={({ field }) => (
                            <FormItem className="flex items-start gap-3">
                              <FormControl>
                                <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {t("auth.terms.prefix")}{" "}
                                <Link to="/legal/terms" className="underline">{t("auth.terms.link")}</Link>
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="accept_privacy"
                          render={({ field }) => (
                            <FormItem className="flex items-start gap-3">
                              <FormControl>
                                <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {t("auth.privacy.prefix")}{" "}
                                <Link to="/legal/privacy" className="underline">{t("auth.privacy.link")}</Link>
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={isLoading}
                      onClick={() => console.log("Company button clicked, form data:", form.getValues())}
                    >
                      {isLoading
                        ? t("auth.btn.loading")
                        : isSignUp
                          ? t("auth.btn.create_company")
                          : t("auth.btn.signin")}
                    </Button>
                    <div className="mt-4">
                      <Button type="button" variant="outline" className="w-full" onClick={handleGoogle}>
                        {t("auth.btn.google")}
                      </Button>
                    </div>
                  </form>
                </Form>
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center">
              <Button
                variant="link"
                onClick={() => setIsSignUp(!isSignUp)}
                className="text-sm"
              >
                {isSignUp ? t("auth.switch.signin") : t("auth.switch.signup")}
              </Button>
              
              {!isSignUp && (
                <div className="mt-4">
                  <Button
                    variant="link"
                    onClick={handleResendVerification}
                    disabled={isLoading}
                    className="text-sm"
                  >
                    {isLoading ? t("auth.resend.sending") : t("auth.resend.text")}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
} 